import {Component, computed, HostListener, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDrawer} from '@angular/material/sidenav';
import {AdminSidePanelComponent} from "@app/manager/admin-main/admin-side-panel/admin-side-panel.component";
import {SharedModule} from "@app/shared/shared.module";
import {AuthStore} from "@app/authentication";
import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle} from "@ng-bootstrap/ng-bootstrap";
import {ContactInfoService} from "@app/core/services/contact-info/contact-info.service";
import {NavigationEnd, Router} from "@angular/router";
import {filter, Subject, takeUntil} from "rxjs";
import {ADMIN_MENU} from "@app/manager/utils/admin-menu.model";

@Component({
  selector: 'ms-admin-main',
  standalone: true,
  imports: [
    SharedModule,
    AdminSidePanelComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu
  ],
  templateUrl: './admin-main.component.html',
  styleUrl: './admin-main.component.scss'
})
export class AdminMainComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatDrawer;

  // États du composant
  hasShowSidePanel = true;
  showBackToTop = false;
  currentYear: number = new Date().getFullYear();
  isLargeScreen = true;
  showMobileSearch = false;
  windowWidth = 0;
  isSearching = false;
  currentRoute = '';

  // Services injectés
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly contactInfoService = inject(ContactInfoService);
  private destroy$ = new Subject<void>();

  user = this.authStore.user;
  contactInfo = this.contactInfoService.getAll();

  // Compteur de notifications (exemple - à remplacer par un vrai service)
  notificationCount = computed(() => 3);

  ngOnInit() {
    this.authStore.profile();

    // Observer les changements de route
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: any) => {
        this.currentRoute = event.urlAfterRedirects;
      });

    // Initialiser la route actuelle
    this.currentRoute = this.router.url;

    // Adapter l'interface sur les appareils mobiles
    this.checkScreenSize();

    // Vérifier si le bouton de retour en haut doit être affiché
    this.checkScrollPosition();

    this.windowWidth = window.innerWidth;
    // Ajouter un écouteur pour les changements de taille d'écran
    window.addEventListener('resize', this.onResize.bind(this));

    // Initialiser l'état de la recherche mobile en fonction de la taille de l'écran
    this.showMobileSearch = false;

    // Ajouter un écouteur pour fermer la recherche mobile quand on clique ailleurs
    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }


  // Détecter le défilement pour afficher/masquer le bouton "retour en haut"
  @HostListener('window:scroll')
  checkScrollPosition() {
    this.showBackToTop = window.scrollY > 300;
  }

  // Détecter les changements de taille d'écran pour ajuster l'interface
  @HostListener('window:resize')
  checkScreenSize() {
    // Déterminer si on est sur un grand écran
    this.isLargeScreen = window.innerWidth >= 1200;

    // Sur mobile, masquer automatiquement le panneau latéral
    if (window.innerWidth < 1200) {
      this.hasShowSidePanel = false;
    } else {
      this.hasShowSidePanel = true;
    }
  }

  // Fonction pour remonter en haut de la page
  scrollToTop() {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }

  // Obtenir le titre de la page actuelle pour le breadcrumb
  getCurrentPageTitle(): string {
    const currentMenu = ADMIN_MENU.find(menu => this.currentRoute.includes(menu.link));
    return currentMenu ? currentMenu.label : 'Tableau de bord';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    window.removeEventListener('resize', this.onResize.bind(this));
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
  }

  toggleMobileSearch(event?: MouseEvent): void {
    // Empêcher la propagation du clic pour éviter que handleOutsideClick ne soit déclenché
    if (event) {
      event.stopPropagation();
    }

    // Basculer l'état de visibilité de la recherche mobile
    this.showMobileSearch = !this.showMobileSearch;

    // Si la recherche est maintenant visible, donner le focus au champ de recherche
    if (this.showMobileSearch) {
      setTimeout(() => {
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }

  logout(): void {
    this.authStore.logout();
  }

  // Méthode pour détecter les changements de taille d'écran
  onResize(): void {
    this.windowWidth = window.innerWidth;

    // Fermer la recherche mobile si l'écran devient plus grand
    if (this.windowWidth >= 576) {
      this.showMobileSearch = false;
    }
  }

  // Fermer la recherche mobile quand on clique en dehors
  handleOutsideClick(event: MouseEvent): void {
    // Ne rien faire si la recherche mobile n'est pas affichée
    if (!this.showMobileSearch) {
      return;
    }

    // Vérifier si le clic est en dehors de la zone de recherche
    const searchBox = document.querySelector('.app-search-box');
    const searchTrigger = document.querySelector('.search-mobile-trigger');

    if (searchBox && searchTrigger) {
      // Vérifier si le clic est à l'intérieur de ces éléments
      const isClickInside = searchBox.contains(event.target as Node) ||
        searchTrigger.contains(event.target as Node);

      // Si le clic est à l'extérieur, fermer la recherche
      if (!isClickInside) {
        this.showMobileSearch = false;
      }
    }
  }

  // Méthode pour gérer la soumission du formulaire de recherche
  performSearch(event: Event): void {
    event.preventDefault();

    // Récupérer la valeur de recherche
    const searchInput = document.querySelector('.search-input') as HTMLInputElement;
    if (searchInput && searchInput.value.trim() !== '') {
      // Activer l'animation de chargement
      this.isSearching = true;

      // Simuler un délai de recherche (à remplacer par votre logique de recherche réelle)
      setTimeout(() => {
        console.log('Recherche pour:', searchInput.value);
        // Désactiver l'animation de chargement après la recherche
        this.isSearching = false;

        // Fermer la recherche mobile après la recherche
        if (this.windowWidth < 576) {
          this.showMobileSearch = false;
        }

        // Ici, vous pouvez naviguer vers une page de résultats ou afficher les résultats
        // this.router.navigate(['/search'], { queryParams: { q: searchInput.value } });
      }, 800);
    }
  }

  // Méthode pour sélectionner une suggestion de recherche
  selectSearchSuggestion(suggestion: string): void {
    // Définir la suggestion comme valeur de recherche
    const searchInput = document.querySelector('.search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = suggestion;

      // Soumettre automatiquement la recherche avec la suggestion
      this.performSearch(new Event('submit'));
    }
  }

}
